import type { MutableRefObject } from 'react';
import biasesCategories from '@data/biasesCategories';
import type { StrapiBiasType } from '@local-types/data';

const globalNavigator = typeof navigator !== 'undefined' && navigator;
const globalWindow = typeof window !== 'undefined' && window;

const isBrowser = () =>
  typeof window !== 'undefined' &&
  typeof navigator !== 'undefined' &&
  typeof document !== 'undefined';

const isAndroid = () =>
  isBrowser() && /(android)/i.test(globalNavigator.userAgent);

const isIOS = () =>
  isBrowser() &&
  !!globalNavigator.userAgent.match(/iPad|iPhone|iPod/i) &&
  // @ts-ignore
  !globalWindow.MSStream;
export const isMobileDevice = () => isIOS() || isAndroid();

const getSectionID = (currentBiasData: StrapiBiasType, id: number) => {
  if (!currentBiasData) return null;

  if (id < 34) {
    return 1;
  } else if (id >= 34 && id < 69) {
    return 2;
  } else if (id >= 69 && id < 98) {
    return 3;
  } else {
    return 4;
  }
};

export const generateLabelsData = (
  biases: any, // TODO - Mary: change to StrapiBiasType[]
  biasNumber: number,
  position: 'left' | 'right',
) => {
  let topBiasNumber: number;
  let bottomBiasNumber: number;

  if (position === 'left') {
    topBiasNumber = biasNumber === 105 ? 999 : biasNumber + 1;
    bottomBiasNumber = biasNumber === 53 ? 999 : biasNumber - 1;
  } else {
    topBiasNumber = biasNumber === 1 ? 999 : biasNumber - 1;
    bottomBiasNumber = biasNumber === 52 ? 999 : biasNumber + 1;
  }
  const biasMapping = biases.map(({ attributes }) => ({
    number: attributes.number,
    bias: attributes,
  }));
  const topBias = biasMapping.find(({ number }) => number === topBiasNumber);
  const currentBias = biasMapping.find(({ number }) => number === biasNumber);
  const bottomBias = biasMapping.find(
    ({ number }) => number === bottomBiasNumber,
  );

  return [
    {
      id: topBiasNumber,
      sectionId: getSectionID(topBias, topBiasNumber),
      data: topBias,
    },
    {
      id: biasNumber,
      sectionId: getSectionID(currentBias, biasNumber),
      data: currentBias,
    },
    {
      id: bottomBiasNumber,
      sectionId: getSectionID(bottomBias, bottomBiasNumber),
      data: bottomBias,
    },
  ];
};

const searchPostfixes = {
  en: [
    ['', 's'],
    ['', ''],
    ['', 's'],
  ],
  ru: [
    ['о', 'ов'],
    ['', ''],
    ['о', 'а'],
  ],
};

const getSearchLabels = (length: number) => {
  const mainNumber = length % 100;
  const lastNumber = mainNumber % 10;

  if (mainNumber >= 10 && mainNumber < 20) {
    return searchPostfixes[0];
  } else {
    if (lastNumber == 1) return searchPostfixes[1];
    else if (lastNumber > 1 && lastNumber < 5) return searchPostfixes[2];
    else return searchPostfixes[0];
  }
};
export const getSearchResults = (biases: any[], searchValue: string) => {
  let results: any[] = [];

  if (searchValue.trim()) {
    results = biases.reduce((acc, { attributes }) => {
      const { number, title, description } = attributes || {};

      if (
        title?.toLowerCase().includes(searchValue.toLowerCase()) ||
        description?.toLowerCase().includes(searchValue.toLowerCase())
      ) {
        acc.push(number);
      }

      return acc;
    }, []);
  }

  const searchLabels = getSearchLabels(results.length);
  return { results, searchLabels };
};

export function groupFilteredData(
  biases: StrapiBiasType[],
  searchResults: any[],
  locale: 'en' | 'ru',
) {
  let groups: any = [];
  let filters = searchResults;

  if (!searchResults.length) {
    // @ts-ignore
    filters = biases.map(bias => bias.number);
  }

  biasesCategories[locale].reduce((acc, cat, index) => {
    filters.forEach(biasNumber => {
      if (biasNumber >= cat.from && biasNumber <= cat.to) {
        if (!acc[index]) acc[index] = [];
        acc[index].push(biasNumber);
      }
    });

    return acc;
  }, groups);

  const sortedGroups = groups.map((group: number[]) =>
    group.sort((a, b) => {
      if (a > b) return 1;
      if (a < b) return -1;
      return 0;
    }),
  );

  return sortedGroups;
}

export const generateSocialLinks = (url: string, title: string) => ({
  linkedIn: `http://www.linkedin.com/shareArticle?url=${url}&title=${title}`,
  facebook: `https://www.facebook.com/sharer/sharer.php?title=${title}&u=${url}`,
  tweeter: `http://twitter.com/share?url=${url}&text=${title}`,
});

export const copyToClipboard = (str: string) => {
  const el = document.createElement('textarea');
  el.value = str;
  document.body.appendChild(el);
  el.select();
  document.execCommand('copy');
  document.body.removeChild(el);
};

export const calculateTooltipPostion = (
  left: number,
  top: number,
  bottom: number,
  blockWidth: number,
  tooltipRef: MutableRefObject<any>,
  isOnBottom: boolean,
) => {
  const { width: tooltipWidth, height: tooltipheight } =
    // @ts-ignore
    tooltipRef.current.getBoundingClientRect();
  // const linkHeight = 17;
  const arrowGap = 6;
  const calculatedTop = isOnBottom
    ? bottom + arrowGap
    : top - tooltipheight - arrowGap;
  /**
   * Left Calculation Formula:
   * half of outside part = (tooltip width - block width) / 2
   * left = block left - half of outside part
   */

  const outsideHalfPart = (tooltipWidth - blockWidth) / 2;
  const calculatedLeft = left - outsideHalfPart;

  return { left: calculatedLeft, top: calculatedTop };
};

export const getAdjacentBiasTitles = (biases, activeBiasNumber) => {
  if (!biases?.data?.length) return { next: null, prev: null }; 

  const biasList = biases.data.map(({ attributes }) => attributes);

  const currentIndex = biasList.findIndex(
    ({ number }) => Number(number) === Number(activeBiasNumber),
  );

  if (currentIndex === -1) return { next: null, prev: null };

  const prevBias = currentIndex === 0 ? biasList[biasList.length - 1] : biasList[currentIndex - 1];
  const nextBias = currentIndex === biasList.length - 1 ? biasList[0] : biasList[currentIndex + 1];
  return {
    prev: prevBias ? prevBias.title : null,
    next: nextBias ? nextBias.title : null,
  };
};


export const slugify = (text: string) =>
  text?.toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^\w-]/g, '')
    .replace(/-{2,}/g, '-');
